import { getReq, postReq, putReq, deleteReq, createUrl } from '@/infra/http';
import { PagedResponseModel, BaseResponseModel, PaginationFilterRequest, PaginatedSelectModel } from '@/models/core.model';
import {
  PointAccount, PointAccountAdjustRequest, PointAccountAssignment, PointAccountAssignmentRequest, PointAccountFundRequest,
  PointAccountList, PointAccountPaginationFilter, PointAccountTransaction, PointEarnRecord, PointEarnRule, UpsertPointAccount,
  UpsertPointEarnRule, pointAccountFromJson, pointAccountListFromJson, pointAccountTransactionFromJson, pointEarnRecordFromJson,
} from '@/models/point-account.model';

const base = 'a/PointAccount';

export const getPagedPointAccounts = async (input: PointAccountPaginationFilter): Promise<PagedResponseModel<PointAccountList>> => {
  return await getReq(createUrl(`${base}/PagedData`), { params: input })
    .then((res: any) => ({
      ...res.data,
      result: res.data.result.map((itm: any) => pointAccountListFromJson(itm)),
    }));
};

export const getPointAccountsLookup = async (input?: string, page?: number): Promise<PaginatedSelectModel> => {
  const model: PointAccountPaginationFilter = {
    pageNumber: page ?? 1,
    pageSize: 100,
    keyword: input,
    orderBy: 'CreatedAt',
    isAscending: false,
  };
  return await getPagedPointAccounts(model).then(data => ({
    data: data.result.map(itm => ({ value: itm.id, label: `${itm.name} (${itm.pointTypeName ?? itm.pointTypeId})` })),
    isLastPage: data.pageNumber >= data.totalPages,
  }));
};

export const getPointAccount = async (id: number): Promise<BaseResponseModel<PointAccount>> => {
  return await getReq(createUrl(`${base}/${id}`))
    .then((res: any) => ({ ...res.data, result: pointAccountFromJson(res.data.result) }));
};

export const upsertPointAccount = async (input: UpsertPointAccount): Promise<BaseResponseModel<PointAccount>> => {
  const { id, ...body } = input;
  const req = id ? putReq(createUrl(`${base}/${id}`), body) : postReq(createUrl(base), body);
  return await req.then((res: any) => res.data);
};

export const fundPointAccount = async (id: number, input: PointAccountFundRequest): Promise<BaseResponseModel<PointAccountTransaction>> => {
  return await postReq(createUrl(`${base}/${id}/Fund`), input).then((res: any) => res.data);
};

export const adjustPointAccount = async (id: number, input: PointAccountAdjustRequest): Promise<BaseResponseModel<PointAccountTransaction>> => {
  return await postReq(createUrl(`${base}/${id}/Adjust`), input).then((res: any) => res.data);
};

export const getPagedPointAccountTransactions = async (id: number, input: PaginationFilterRequest): Promise<PagedResponseModel<PointAccountTransaction>> => {
  return await getReq(createUrl(`${base}/${id}/Transaction/PagedData`), { params: input })
    .then((res: any) => ({
      ...res.data,
      result: res.data.result.map((itm: any) => pointAccountTransactionFromJson(itm)),
    }));
};

export const addPointAccountAssignment = async (id: number, input: PointAccountAssignmentRequest): Promise<BaseResponseModel<PointAccountAssignment>> => {
  return await postReq(createUrl(`${base}/${id}/Assignment`), input).then((res: any) => res.data);
};

export const removePointAccountAssignment = async (id: number, assignmentId: number): Promise<BaseResponseModel<null>> => {
  return await deleteReq(createUrl(`${base}/${id}/Assignment/${assignmentId}`)).then((res: any) => res.data);
};

export const getPointEarnRules = async (id: number): Promise<BaseResponseModel<PointEarnRule[]>> => {
  return await getReq(createUrl(`${base}/${id}/Rule`)).then((res: any) => res.data);
};

export const createPointEarnRule = async (id: number, input: UpsertPointEarnRule): Promise<BaseResponseModel<PointEarnRule>> => {
  return await postReq(createUrl(`${base}/${id}/Rule`), input).then((res: any) => res.data);
};

export const updatePointEarnRule = async (id: number, ruleId: number, input: UpsertPointEarnRule): Promise<BaseResponseModel<PointEarnRule>> => {
  return await putReq(createUrl(`${base}/${id}/Rule/${ruleId}`), input).then((res: any) => res.data);
};

export const setPointEarnRuleActive = async (id: number, ruleId: number, isActive: boolean): Promise<BaseResponseModel<PointEarnRule>> => {
  return await putReq(createUrl(`${base}/${id}/Rule/${ruleId}/Active`), { isActive }).then((res: any) => res.data);
};

export const getPagedPointEarnRecords = async (id: number, ruleId: number, input: PaginationFilterRequest): Promise<PagedResponseModel<PointEarnRecord>> => {
  return await getReq(createUrl(`${base}/${id}/Rule/${ruleId}/Record/PagedData`), { params: input })
    .then((res: any) => ({
      ...res.data,
      result: res.data.result.map((itm: any) => pointEarnRecordFromJson(itm)),
    }));
};
